// src/components/help.jsx — FINAL ESDRAS HELP/FEEDBACK (for users & stylists – admin resolves)
import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';

const NAVY = '#001F3F';
const GOLD = '#B8860B';

export default function Help({ onClose }) {
  const [issue, setIssue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const auth = getAuth();
  const user = auth.currentUser;

  const handleSubmit = async (e) => {
    e?.preventDefault(); // Handle both button click and form submit

    if (!user) {
      setError('Please log in to submit feedback');
      return;
    }

    if (!issue.trim()) {
      setError('Please describe your issue');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await addDoc(collection(db, 'feedback'), {
        userId: user.uid,
        userEmail: user.email || null,
        displayName: user.displayName || null,
        issue: issue.trim(),
        status: 'pending',
        createdAt: serverTimestamp(),
      });

      alert('Thanks! Our team will resolve this soon 👑');
      setIssue(''); // Reset form
      onClose?.(); // Safe onClose call
    } catch (err) {
      console.error('Feedback submission error:', err);
      setError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.95)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 9999,
        padding: '1rem',
      }}
      onClick={(e) => {
        // Close on backdrop click
        if (e.target === e.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div
        style={{
          background: NAVY,
          color: 'white',
          padding: '3rem 2.5rem',
          borderRadius: '28px',
          border: `4px solid ${GOLD}`,
          maxWidth: '90vw',
          maxHeight: '90vh',
          width: '100%',
          maxWidth: '500px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
          fontFamily: 'Montserrat, sans-serif',
          textAlign: 'center',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <h2
          style={{
            color: GOLD,
            fontSize: '2.2rem',
            fontWeight: '800',
            margin: '0 0 1rem 0',
            lineHeight: 1.2,
          }}
        >
          Help & Feedback
        </h2>

        <p
          style={{
            opacity: 0.9,
            marginBottom: '2rem',
            fontSize: '1.1rem',
            lineHeight: 1.6,
          }}
        >
          Tell us what's wrong – we'll fix it fast
          <br />
          <span style={{ color: GOLD, fontWeight: '600' }}>
            Users & Stylists: Admin team resolves all
          </span>
        </p>

        {/* Error Message */}
        {error && (
          <p
            style={{
              color: '#ff6b6b',
              background: 'rgba(255, 107, 107, 0.1)',
              padding: '1rem',
              borderRadius: '12px',
              marginBottom: '1.5rem',
              border: '1px solid rgba(255, 107, 107, 0.3)',
            }}
          >
            {error}
          </p>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <textarea
            placeholder="Describe the issue (e.g., 'Style generation failed', 'Payment issue', 'Feature request')..."
            value={issue}
            onChange={(e) => setIssue(e.target.value)}
            rows={5}
            maxLength={2000}
            disabled={submitting}
            style={{
              width: '100%',
              height: '160px',
              padding: '1.25rem',
              borderRadius: '16px',
              background: 'rgba(255, 255, 255, 0.08)',
              border: `2px solid rgba(184, 134, 11, 0.3)`,
              color: 'white',
              fontSize: '1.1rem',
              fontFamily: 'Montserrat, sans-serif',
              resize: 'vertical',
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(10px)',
            }}
            style={
              // Dynamic border color based on state
              issue.trim()
                ? { borderColor: `rgba(184, 134, 11, 0.6)` }
                : { borderColor: `rgba(255, 255, 255, 0.2)` }
            }
          />
          
          <div style={{ marginTop: '1rem', fontSize: '0.85rem', opacity: 0.7 }}>
            {issue.length}/2000 characters
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '2rem' }}>
            <button
              type="submit"
              disabled={submitting || !issue.trim() || !user}
              style={{
                background: GOLD,
                color: 'black',
                padding: '1.25rem 3rem',
                border: 'none',
                borderRadius: '50px',
                fontSize: '1.3rem',
                fontWeight: '800',
                fontFamily: 'Montserrat, sans-serif',
                cursor: submitting || !issue.trim() || !user ? 'not-allowed' : 'pointer',
                opacity: (submitting || !issue.trim() || !user) ? 0.6 : 1,
                minWidth: '140px',
                transition: 'all 0.2s ease',
                boxShadow: '0 10px 25px rgba(184, 134, 11, 0.4)',
              }}
              onMouseDown={(e) => {
                if (!submitting && issue.trim() && user) {
                  e.target.style.transform = 'translateY(2px)';
                }
              }}
              onMouseUp={(e) => e.target.style.transform = ''}
              onMouseLeave={(e) => e.target.style.transform = ''}
            >
              {submitting ? 'Sending...' : 'Send Feedback'}
            </button>

            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                color: '#ccc',
                background: 'none',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                padding: '1rem 2rem',
                borderRadius: '50px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.5 : 1,
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(10px)',
              }}
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Footer */}
        <p
          style={{
            marginTop: '2rem',
            paddingTop: '1.5rem',
            borderTop: `1px solid rgba(184, 134, 11, 0.2)`,
            opacity: 0.7,
            fontSize: '0.9rem',
          }}
        >
          Secure • Anonymous option • Response within 24hrs
        </p>
      </div>
    </div>
  );
}